import { useEffect } from 'react';

/**
 * 原生化特性 Hook
 * 提供接近原生 App 的用户体验
 */
export function useNativeFeatures() {
  useEffect(() => {
    // 1. 禁用橡皮筋效果 (iOS)
    document.body.style.overscrollBehavior = 'none';
    
    // 2. 添加触摸反馈优化
    document.addEventListener('touchstart', () => {}, { passive: true });
    
    // 3. 硬件加速 - 注意：will-change: transform 会创建新的包含块，影响 fixed 定位
    // 因此这里不再设置 will-change，改用 CSS 在需要的元素上单独设置
    
    // 4. 优化视口设置
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
      );
    }
    
    // 5. 处理安全区域 (iPhone X+)
    const setSafeAreaInsets = () => {
      const root = document.documentElement;
      root.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top)');
      root.style.setProperty('--safe-area-inset-right', 'env(safe-area-inset-right)');
      root.style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom)');
      root.style.setProperty('--safe-area-inset-left', 'env(safe-area-inset-left)');
    };
    setSafeAreaInsets();
    
    // 6. 禁用双击缩放
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (event) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, false);
    
    // 7. 添加原生滚动优化
    const scrollableElements = document.querySelectorAll('.scrollable');
    scrollableElements.forEach(el => {
      const element = el as HTMLElement;
      // @ts-expect-error - webkit 前缀属性
      element.style.webkitOverflowScrolling = 'touch';
    });
    
    // 8. 处理状态栏样式
    const updateStatusBar = () => {
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        metaThemeColor.setAttribute('content', isDarkMode ? '#000000' : '#ffffff');
      }
    };
    updateStatusBar();
    
    // 监听主题变化
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateStatusBar);
    
    // 清理函数
    return () => {
      mediaQuery.removeEventListener('change', updateStatusBar);
    };
  }, []);
  
  // 返回一些有用的原生特性方法
  return {
    // 触发触觉反馈 (如果支持)
    vibrate: (pattern: number | number[] = 10) => {
      if ('vibrate' in navigator) {
        navigator.vibrate(pattern);
      }
    },
    
    // 全屏切换
    toggleFullscreen: async () => {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    },
    
    // 检查是否为 PWA 模式
    isStandalone: () => {
      return window.matchMedia('(display-mode: standalone)').matches ||
             (window.navigator as Navigator & { standalone?: boolean }).standalone ||
             document.referrer.includes('android-app://');
    },
    
    // 请求通知权限
    requestNotificationPermission: async () => {
      if ('Notification' in window) {
        return await Notification.requestPermission();
      }
      return 'denied';
    }
  };
}